<template>
    <BaseLink
        :to="props.to"
        :externalTo="props.externalTo"
        :target="props.target"
        :class="[
            $style.link,
            props.color && $style[`color_${props.color}`],
            props.hoverAnimation && $style[`hover_animation_${props.hoverAnimation}`],
            props.disabled ? $style.disabled : '',
        ]"
        :disabled="props.disabled"
        @click="props.onClick"
    >
        <slot />
    </BaseLink>
</template>

<script lang="ts" setup>
import BaseLink, { type BaseLinkProps } from '@/components_new/BaseLink.vue'

export interface LinkProps extends BaseLinkProps {
    color?: 'purple-500' | 'purple-600' | 'golden' | 'white' | 'white-60' | 'white-80' | 'white-40' | 'rainbow'
    hoverAnimation?: 'rainbow'
}

const props = defineProps<LinkProps>()
</script>

<style module>
/* Color themes */

.color_purple-500 {
    --text: #aa76ff;
    --text-hover: #c0ff47;
}

.color_purple-600 {
    --text: #c7abff;
    --text-hover: #c0ff47;
}

.color_golden {
    --text: #ffdca1;
    --text-hover: #ffc850;
}

.color_white {
    --text: #fff;
    --text-hover: #e6d4ff;
}

.color_white-80 {
    --text: var(--white-80);
    --text-hover: #e6d4ff;
}

.color_white-60 {
    --text: var(--white-60);
    --text-hover: #e6d4ff;
}

.color_white-40 {
    --text: var(--white-40);
    --text-hover: #e6d4ff;
}

.color_rainbow {
    --text: var(--black);
    --text-hover: var(--black);
    animation: color-change 3500ms steps(10, end) infinite;
}

@keyframes color-change {
    0% {
        background-color: var(--electric-lime);
    }
    10% {
        background-color: #ffc850;
    }
    20% {
        background-color: #9ce52e;
    }
    30% {
        background-color: #b099d0;
    }
    40% {
        background-color: #ff9c9c;
    }
    50% {
        background-color: blue;
    }
    60% {
        background-color: indigo;
    }
    70% {
        background-color: violet;
    }
    80% {
        background-color: pink;
    }
    90% {
        background-color: crimson;
    }
    100% {
        background-color: var(--electric-lime);
    }
}

.link.hover_animation_rainbow:hover {
    animation: color-change 3500ms steps(10, end) infinite;
    --text-hover: var(--black);
}

.link {
    color: var(--text);
}

.link:hover,
.link:focus-visible,
.link:has(:focus-visible),
.link:active {
    color: var(--text-hover, inherit);
}

.link:focus-visible {
    color: var(--text-hover, inherit);
    outline: none;
}

.link.disabled,
.link.disabled:hover,
.link.disabled:active,
.link.disabled:focus-visible,
.link.disabled:has(:focus-visible) {
    color: var(--text);
    cursor: default;
}
</style>
